import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  actualTheme: 'light' | 'dark'; // Gerçekte kullanılan tema (system ise çözümlenmiş hali)
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      return savedTheme;
    }
    // İlk açılışta her zaman aydınlık modda başla
    return 'light';
  });

  // Gerçekte kullanılan temayı hesapla
  const getActualTheme = (): 'light' | 'dark' => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  };

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>(getActualTheme);

  // Tema ve actualTheme'i güncelle
  useEffect(() => {
    const newActualTheme = getActualTheme();
    setActualTheme(newActualTheme);
    
    localStorage.setItem('theme', theme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(newActualTheme);
    
    // Meta theme-color'ı dinamik olarak güncelle
    const updateThemeColor = () => {
      // Mevcut theme-color meta tag'lerini kaldır
      const existingMetaTags = document.querySelectorAll('meta[name="theme-color"]');
      existingMetaTags.forEach(tag => tag.remove());
      
      // Yeni theme-color meta tag'ini ekle
      const metaTag = document.createElement('meta');
      metaTag.name = 'theme-color';
      metaTag.content = newActualTheme === 'light' ? '#ffffff' : '#1f2937';
      document.head.appendChild(metaTag);
      
      // Apple status bar için ek meta tag güncelle - PWA'da temaya göre ayarla
      const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (appleStatusBarMeta) {
        // Temaya göre status bar stilini ayarla
        appleStatusBarMeta.setAttribute('content', newActualTheme === 'light' ? 'default' : 'black');
      }
      
      // Microsoft tile color güncelle
      const msTileColorMeta = document.querySelector('meta[name="msapplication-TileColor"]');
      if (msTileColorMeta) {
        msTileColorMeta.setAttribute('content', newActualTheme === 'light' ? '#ffffff' : '#1f2937');
      }
    };
    
    updateThemeColor();
  }, [theme]);

  // Sistem tema değişikliği dinleyicisi - sadece 'system' modundayken aktif
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Sadece 'system' modundayken sistem tema değişikliğini takip et
      if (theme === 'system') {
        const newActualTheme = getActualTheme();
        setActualTheme(newActualTheme);
        
        // DOM'u güncelle
        document.documentElement.classList.remove('light', 'dark');
        document.documentElement.classList.add(newActualTheme);
        
        // Meta tags'leri güncelle
        const existingMetaTags = document.querySelectorAll('meta[name="theme-color"]');
        existingMetaTags.forEach(tag => tag.remove());
        
        const metaTag = document.createElement('meta');
        metaTag.name = 'theme-color';
        metaTag.content = newActualTheme === 'light' ? '#ffffff' : '#1f2937';
        document.head.appendChild(metaTag);
        
        const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
        if (appleStatusBarMeta) {
          // Temaya göre status bar stilini ayarla
          appleStatusBarMeta.setAttribute('content', newActualTheme === 'light' ? 'default' : 'black');
        }
      }
    };

    // Sadece system modundayken listener ekle
    if (theme === 'system') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => {
      // Cycle: light -> dark -> system -> light
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, actualTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
} 